import React, { useEffect, useState } from "react";
import { Alert, Button, FormControl, Snackbar, Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CheckIcon from '@mui/icons-material/Check';
import UnitSelect from "./UnitSelect";
import { Item } from "../Services/Item/ItemApi";
import { RecordedPrice } from "../Services/RecordedPrice/RecordedPriceApi";
import { useAddRecordedPrice } from "../Services/RecordedPrice/RecordedPriceHooks";
import { Store } from "../Services/Store/StoreApi";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";

interface EditRecordFormProps {
    item: Item;
    store: Store;
    onSaved: () => void;
}

function defaultRecord(store: Store, item: Item) {
    return {
        price: 0,
        quantity: item.defaultQuantity ?? 0,
        description: "",
        endDate: undefined,
        unit: item.defaultUnit!,
        store: store,
        item: item,
    }
}

export default function EditRecordForm({ item, store, onSaved }: EditRecordFormProps) {

    const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm<RecordedPrice>({
        defaultValues: defaultRecord(store, item)
    });



    useEffect(() => {
        reset(defaultRecord(store, item));
    }, [reset, item, store]);

    const addPriceMutation = useAddRecordedPrice();

    const onSubmit = (newRecord: RecordedPrice) => {
        addPriceMutation.mutate(newRecord, {
            onSuccess: () => {
                setDisplaySuccessBar(true);
                reset(defaultRecord(store, item));
                addPriceMutation.reset();
                onSaved();
            }
        });
    };

    const [displaySuccessBar, setDisplaySuccessBar] = useState(false);
    const handleSnackbarClose = () => setDisplaySuccessBar(false);

    return (
        <Stack spacing={1 }>

            <TextField
                fullWidth
                label="Price"
                type="number"
                {...register("price", { required: "Price is required", min: { value: 0.01, message: "Price must be greater than 0" } })}
                error={!!errors.price}
                helperText={errors.price ? errors.price.message : ""}
            />
            <TextField
                fullWidth
                label="Quantity"
                type="number"
                {...register("quantity", { required: "Quantity is required", min: { value: 1, message: "Quantity must be greater than 0" } })}
                error={!!errors.quantity}
                helperText={errors.quantity ? errors.quantity.message : ""}
            />
            <FormControl fullWidth>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            label="Description"
                            {...field}
                            rows={3}
                            error={!!errors.description}
                            helperText={errors.description ? errors.description.message : ""}
                        />
                    )}
                />
            </FormControl>
            
            <FormControl fullWidth>
                <Controller
                    name="unit"
                    control={control}
                    rules={{ required: "Unit is required" }}
                    render={({ field, fieldState }) => (
                        <UnitSelect
                            {...field}
                            error={fieldState.error}
                        />
                    )}
                />
                {errors.unit && <p style={{ color: 'red' }}>Unit is required</p>}
            </FormControl>
            <FormControl fullWidth>
                <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="End Date"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(date) => field.onChange(date ? date.toDate() : null)}
                            inputRef={field.ref}
                            slotProps={{
                                field: { clearable: true },
                            }}
                        />
                    )}
                />

            </FormControl>
            <Button onClick={handleSubmit(onSubmit)}>Save</Button>
            <Snackbar
                open={displaySuccessBar}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
            >
                <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
                    Price Added Successfully
                </Alert>
            </Snackbar>
        </Stack>
    );
}